import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * Directions to the entrance, for people who haven't arrived yet. The
 * email's photos and turns are for the AHMA entrance only, so the page
 * offers it only for events there.
 */
export function DirectionsPanel({
  eventSlug,
  audienceCount,
  defaultTestEmail,
  senderName,
  alreadySentNote,
}: {
  eventSlug: string;
  /** Addresses that haven't been checked in. */
  audienceCount: number;
  defaultTestEmail: string;
  senderName: string | null;
  alreadySentNote: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const directions = trpc.adminSendEventDirections.useMutation();

  const send = (mode: "test" | "all") => {
    directions.mutate(
      {
        eventSlug,
        mode,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : `Test sent to ${res.sentTo}. Check the photos and directions look right, then send to everyone still on their way.`,
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "Everyone who booked has arrived, so there's no one to send this to.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  return (
    <>
      <p className={BODY_TEXT}>
        Photos and step-by-step directions to the AHMA entrance, for people who
        haven't arrived yet.
      </p>
      <TestThenSend
        flow={flow}
        emailName="Directions to the entrance"
        audience={audienceCount > 0 ? people(audienceCount) : null}
        noAudienceText="Everyone who booked has arrived, so there's no one to send this to."
        busy={directions.isPending}
        onSendTest={() => send("test")}
        onSendAll={() => send("all")}
        alreadySentNote={alreadySentNote}
      />
    </>
  );
}
